/**
 * Progressive Relaxation System for Normalized Matchmaking
 * 
 * When no valid match is found at strict settings, progressively relax
 * hard blocks one at a time until a match is found.
 * 
 * Architecture:
 * - Configurable relaxation order (9 levels total)
 * - Partnership violations are NEVER relaxed
 * - Preset configurations for different session types
 * - Database-ready for per-session customization
 * - Transparent reporting of which relaxations were used
 * 
 * Relaxation Order (defaults):
 * Level 0: STRICT - All hard blocks active
 * Level 1: Relax category gaps (3+ → 4+ tier differences)
 * Level 2: Relax cooldown
 * Level 3: (legacy exactRepeat — disabled; quartet variety is soft-scored in interactions)
 * Level 4: Relax session interaction density (pairs who shared most of their games)
 * Level 5: Relax immediate same-team repeat
 * Level 6: Relax back-to-back block (allow 3rd consecutive personal game)
 * Level 7: Relax consecutive 4th+ interactions
 * Level 8: Relax min+2 fairness guard (only when no match found at stricter levels)
 * Level 9: Relax game type restrictions (LAST RESORT)
 * 
 * Partnership violations: NEVER RELAXED at any level
 */

/**
 * Default relaxation configuration
 * These are the baseline settings that work well for most sessions
 * Hosts can override these per-session to match their preferences
 */
export const DEFAULT_RELAXATION_CONFIG = {
  // Enable/disable progressive relaxation entirely
  enabled: true,
  
  // Maximum relaxation level to try (0 = strict only, 9 = try all)
  maxLevel: 9,
  
  // Relaxation levels in priority order (try first → try last)
  // Lower number = try first, higher number = try last
  relaxationOrder: [
    { type: 'categoryGap', priority: 1 },
    { type: 'cooldown', priority: 2 },
    { type: 'exactRepeat', priority: 3 },
    { type: 'sessionInteractionDensity', priority: 4 },
    { type: 'immediateRepeat', priority: 5 },
    { type: 'consecutive3', priority: 6 },
    { type: 'consecutive4', priority: 7 },
    { type: 'minPlus2', priority: 8 },
    { type: 'gameType', priority: 9 },
  ],
  
  // Individual relaxation settings
  relaxations: {
    categoryGap: {
      enabled: true,
      priority: 1,
      name: 'Allow Large Skill Gaps',
      description: 'Permit matches between players 4+ skill tiers apart (e.g., A vs E)',
      hardBlockChange: 'categoryGap: 3 → null (4+ allowed)',
    },

    cooldown: {
      enabled: true,
      priority: 2,
      name: 'Relax Cooldown Requirements',
      description: 'Include players who haven\'t observed enough courts (if cooldown enabled)',
      hardBlockChange: 'enforceCooldown: true → false',
    },

    minPlus2: {
      enabled: true,
      priority: 8,
      name: 'Relax Min+2 Fairness Guard',
      description: 'Allow players already at min+2+ to play when no stricter match exists (deadlock only)',
      hardBlockChange: 'minPlus2Guard: true → false',
    },

    sessionInteractionDensity: {
      enabled: true,
      priority: 4,
      name: 'Allow High Session Interaction Density',
      description: 'Permit pairs who have already shared most of their session games (strict blocks ~every-game overlap)',
      hardBlockChange: 'enforceSessionInteractionDensity: true → false',
    },

    immediateRepeat: {
      enabled: true,
      priority: 5,
      name: 'Allow Immediate Same-Team Repeat',
      description: 'Same pair can play together again in next game (partnerships exempt)',
      hardBlockChange: 'allowImmediateRepeat: false → true',
    },

    consecutive3: {
      enabled: true,
      priority: 6,
      name: 'Allow 3rd Consecutive Interaction',
      description: 'Players can interact 3 games in a row (strict blocks when personal streak >= 2)',
      hardBlockChange: 'consecutiveThreshold: 2 → 3 (allow 3rd personal consecutive, block 4th)',
    },

    consecutive4: {
      enabled: true,
      priority: 7,
      name: 'Allow 4th+ Consecutive Interaction',
      description: 'Players can interact 4+ times in a row',
      hardBlockChange: 'consecutiveThreshold: 3 → null',
    },

    exactRepeat: {
      enabled: false,
      priority: 3,
      name: 'Allow Exact Repeat Games',
      description: 'Deprecated — quartet/identical repeats are soft-scored in the interactions dimension',
      hardBlockChange: 'disabled (soft scoring only)',
    },
    
    gameType: {
      enabled: true,
      priority: 9,
      name: 'Relax Game Type Restrictions',
      description: 'Allow game types that violate session configuration (LAST RESORT)',
      hardBlockChange: 'enforceGameType: true → false',
    },
  },
};

/**
 * Convert relaxation config to hard block settings at a given level
 * @param {Object} relaxationConfig - Relaxation configuration
 * @param {number} level - Current relaxation step count (0 = strict; capped by configured relaxations × maxLevel)
 * @param {boolean} cooldownEnabled - Session-level cooldown setting
 * @returns {Object} Hard block settings for scoring functions
 */
export function getHardBlocksForLevel(relaxationConfig, level, cooldownEnabled = true) {
  const config = relaxationConfig || DEFAULT_RELAXATION_CONFIG;
  
  // Start with strict settings (Level 0)
  const hardBlocks = {
    // NEVER RELAXED - always enforced
    partnershipViolations: false,  // false = enforce, true = allow (but we never allow)
    
    // Progressively relaxed
    categoryGap: 3,                 // Block 4+ tier gaps at strict
    consecutiveThreshold: 2,        // Block 3rd consecutive personal game together at strict (streak >= 2)
    enforceSessionInteractionDensity: true, // Block pairs who share most session games at strict
    allowExactRepeat: false,        // Block exact repeats at strict
    allowImmediateRepeat: false,    // Block immediate same-team repeat at strict
    allowRecentRepeat: false,       // Block repeat teams within recent window at strict
    repeatTeamWindow: 6,            // Block same pair until each has played 6+ games since last together (per-player)
    minPlus2Guard: true,            // Enforce min+2 guard at strict
    enforceCooldown: cooldownEnabled, // Enforce if session has cooldown enabled
    enforceGameType: true,          // Enforce game type restrictions at strict
  };
  
  // If level 0 (strict), return as-is
  if (level === 0) {
    return { ...hardBlocks, level: 0, relaxationName: 'Strict' };
  }
  
  // Get sorted relaxation order
  const sortedRelaxations = [...config.relaxationOrder]
    .filter(r => {
      if (r.type === 'consecutive4AndExactRepeat') {
        // Legacy bundled step applies when both component relaxations remain enabled
        return !!(
          config.relaxations.consecutive4?.enabled &&
          config.relaxations.exactRepeat?.enabled
        );
      }
      return config.relaxations[r.type]?.enabled;
    })
    .sort((a, b) => a.priority - b.priority);
  
  // Apply relaxations up to current level
  let appliedRelaxations = [];
  
  for (let i = 0; i < Math.min(level, sortedRelaxations.length); i++) {
    const relaxation = sortedRelaxations[i];
    const relaxationType = relaxation.type;
    
    switch (relaxationType) {
      case 'categoryGap':
        hardBlocks.categoryGap = null; // Allow any category gap (4+)
        appliedRelaxations.push(config.relaxations.categoryGap.name);
        break;
        
      case 'sessionInteractionDensity':
        hardBlocks.enforceSessionInteractionDensity = false;
        appliedRelaxations.push(config.relaxations.sessionInteractionDensity.name);
        break;

      case 'consecutive3':
        hardBlocks.consecutiveThreshold = 3; // Allow 3rd consecutive, block 4th (streak >= 3)
        appliedRelaxations.push(config.relaxations.consecutive3.name);
        break;
        
      case 'immediateRepeat':
        hardBlocks.allowImmediateRepeat = true; // Allow immediate same-team
        hardBlocks.allowRecentRepeat = true;    // Also allow recent repeat window
        appliedRelaxations.push(config.relaxations.immediateRepeat.name);
        break;
        
      case 'consecutive4':
        hardBlocks.consecutiveThreshold = null; // Allow 4+ consecutive
        appliedRelaxations.push(config.relaxations.consecutive4.name);
        break;
        
      case 'exactRepeat':
        hardBlocks.allowExactRepeat = true;
        appliedRelaxations.push(config.relaxations.exactRepeat.name);
        break;

      // Legacy persisted configs: combined step applies both thresholds
      case 'consecutive4AndExactRepeat':
        hardBlocks.consecutiveThreshold = null;
        hardBlocks.allowExactRepeat = true;
        appliedRelaxations.push(
          `${config.relaxations.consecutive4.name} + ${config.relaxations.exactRepeat.name}`,
        );
        break;
        
      case 'minPlus2':
        hardBlocks.minPlus2Guard = false; // Allow min+3, min+4, etc.
        appliedRelaxations.push(config.relaxations.minPlus2.name);
        break;
        
      case 'cooldown':
        hardBlocks.enforceCooldown = false; // Relax cooldown requirements
        appliedRelaxations.push(config.relaxations.cooldown.name);
        break;
        
      case 'gameType':
        hardBlocks.enforceGameType = false; // Relax game type
        appliedRelaxations.push(config.relaxations.gameType.name);
        break;

      default:
        break;
    }
  }
  
  return {
    ...hardBlocks,
    level,
    relaxationName: appliedRelaxations.join(' + '),
    appliedRelaxations,
  };
}

/**
 * Get the maximum number of relaxation levels based on config
 * @param {Object} relaxationConfig - Relaxation configuration
 * @returns {number} Highest level index usable (0 = strict only). Each step corresponds to one entry in relaxation order.
 */
export function getMaxRelaxationLevel(relaxationConfig) {
  const config = relaxationConfig || DEFAULT_RELAXATION_CONFIG;
  
  if (!config.enabled) {
    return 0; // Only strict level
  }
  
  const enabledRelaxations = getEnabledRelaxationOrder(config);
  
  // 0 = strict, then one level per enabled relaxation
  return Math.min(enabledRelaxations.length, config.maxLevel);
}

/**
 * Enabled relaxation steps in priority order (same filter as getHardBlocksForLevel).
 * @param {Object} relaxationConfig
 * @returns {Array<{ type: string, priority: number }>}
 */
export function getEnabledRelaxationOrder(relaxationConfig) {
  const config = relaxationConfig || DEFAULT_RELAXATION_CONFIG;
  return [...config.relaxationOrder]
    .filter((r) => {
      if (r.type === 'consecutive4AndExactRepeat') {
        return !!(
          config.relaxations.consecutive4?.enabled &&
          config.relaxations.exactRepeat?.enabled
        );
      }
      return config.relaxations[r.type]?.enabled;
    })
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Relaxation level index at which a given step first applies (level 0 = strict).
 * @param {Object} relaxationConfig
 * @param {string} relaxationType
 * @returns {number|null} null when the type is disabled
 */
export function getRelaxationLevelForType(relaxationConfig, relaxationType) {
  const order = getEnabledRelaxationOrder(relaxationConfig);
  const idx = order.findIndex((r) => r.type === relaxationType);
  return idx < 0 ? null : idx + 1;
}

/** Max level before consecutive3 (immediateRepeat still allowed). */
export function getPreConsecutiveMaxRelaxationLevel(relaxationConfig) {
  const consecutiveLevel = getRelaxationLevelForType(relaxationConfig, 'consecutive3');
  if (consecutiveLevel == null) {
    return getMaxRelaxationLevel(relaxationConfig);
  }
  return consecutiveLevel - 1;
}

/** First level where consecutive3 applies. */
export function getConsecutiveMinRelaxationLevel(relaxationConfig) {
  const consecutiveLevel = getRelaxationLevelForType(relaxationConfig, 'consecutive3');
  if (consecutiveLevel == null) {
    return getMaxRelaxationLevel(relaxationConfig) + 1;
  }
  return consecutiveLevel;
}

/**
 * Get user-friendly message for relaxation level
 * @param {number} level - Relaxation level
 * @param {Object} hardBlocks - Hard block settings returned from getHardBlocksForLevel
 * @returns {Object} Message info { severity, icon, message }
 */
export function getRelaxationMessage(level, hardBlocks) {
  if (level === 0) {
    return {
      severity: 'none',
      icon: '',
      message: '',
    };
  }
  
  // Game type relaxation is most severe (violates user intent)
  if (!hardBlocks.enforceGameType) {
    return {
      severity: 'critical',
      icon: '🚨',
      message: `Game type restriction relaxed to keep session moving (${hardBlocks.relaxationName})`,
    };
  }
  
  // Min+2 and cooldown relaxation are significant
  const cooledDownViaRelaxation =
    level > 0 &&
    Array.isArray(hardBlocks.appliedRelaxations) &&
    hardBlocks.appliedRelaxations.some(n => typeof n === 'string' && /cooldown/i.test(n));

  if (!hardBlocks.minPlus2Guard || cooledDownViaRelaxation) {
    return {
      severity: 'warning',
      icon: '⚠️',
      message: `Fairness constraints relaxed: ${hardBlocks.relaxationName}`,
    };
  }
  
  // Session density, exact repeats, and immediate repeats are noticeable
  if (
    hardBlocks.enforceSessionInteractionDensity === false ||
    hardBlocks.allowExactRepeat ||
    hardBlocks.allowImmediateRepeat
  ) {
    return {
      severity: 'info',
      icon: 'ℹ️',
      message: `Variety rules relaxed: ${hardBlocks.relaxationName}`,
    };
  }
  
  // Other relaxations are minor
  return {
    severity: 'info',
    icon: 'ℹ️',
    message: `Minor matching adjustment: ${hardBlocks.relaxationName}`,
  };
}

/**
 * Validate a relaxation configuration
 * @param {Object} config - Relaxation configuration to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateRelaxationConfig(config) {
  const errors = [];
  
  if (!config) {
    errors.push('Config is required');
    return { valid: false, errors };
  }
  
  // Check required fields
  if (typeof config.enabled !== 'boolean') {
    errors.push('enabled must be a boolean');
  }
  
  if (typeof config.maxLevel !== 'number' || config.maxLevel < 0 || config.maxLevel > 9) {
    errors.push('maxLevel must be a number between 0 and 9');
  }
  
  if (!Array.isArray(config.relaxationOrder)) {
    errors.push('relaxationOrder must be an array');
  }
  
  if (!config.relaxations || typeof config.relaxations !== 'object') {
    errors.push('relaxations must be an object');
  }
  
  // Check relaxation order
  if (config.relaxationOrder) {
    const validTypes = [
      'categoryGap',
      'sessionInteractionDensity',
      'consecutive3',
      'immediateRepeat',
      'consecutive4',
      'exactRepeat',
      'minPlus2',
      'cooldown',
      'gameType',
      'consecutive4AndExactRepeat', // legacy persisted orders
    ];
    config.relaxationOrder.forEach((item, idx) => {
      if (!item.type || !validTypes.includes(item.type)) {
        errors.push(`relaxationOrder[${idx}].type must be one of: ${validTypes.join(', ')}`);
      }
      if (typeof item.priority !== 'number' || item.priority < 1 || item.priority > 9) {
        errors.push(`relaxationOrder[${idx}].priority must be between 1 and 9`);
      }
    });
    
    // Check for duplicate priorities
    const priorities = config.relaxationOrder.map(r => r.priority);
    const uniquePriorities = new Set(priorities);
    if (priorities.length !== uniquePriorities.size) {
      errors.push('Duplicate priorities found in relaxationOrder - each must be unique');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Example of how to use custom relaxation order (for future host configuration)
 * 
 * @example
 * // Host creates custom order via UI
 * const hostCustomConfig = {
 *   enabled: true,
 *   maxLevel: 8,
 *   relaxationOrder: [
 *     { type: 'categoryGap', priority: 1 },
 *     { type: 'cooldown', priority: 2 },
 *     { type: 'minPlus2', priority: 3 },
 *     { type: 'exactRepeat', priority: 4 },
 *     { type: 'consecutive3', priority: 5 },
 *     { type: 'consecutive4', priority: 6 },
 *     { type: 'immediateRepeat', priority: 7 },
 *     { type: 'gameType', priority: 8 },
 *   ],
 *   relaxations: { ...DEFAULT_RELAXATION_CONFIG.relaxations },
 * };
 * 
 * // Save to database
 * await supabase
 *   .from('sessions')
 *   .update({ 
 *     relaxation_config: JSON.stringify(hostCustomConfig),
 *     cooldown_enabled: true,  // Session-level cooldown setting
 *   })
 *   .eq('id', sessionId);
 * 
 * // Load and use
 * const session = await getSession(sessionId);
 * const config = session.relaxation_config 
 *   ? JSON.parse(session.relaxation_config)
 *   : DEFAULT_RELAXATION_CONFIG;
 * 
 * const hardBlocks = getHardBlocksForLevel(config, currentLevel, session.cooldown_enabled);
 */
